import type {
	App,
	Directive,
	DirectiveBinding,
} from 'vue';

const HANDLERS_PROPERTY = '__v-click-outside';
const IGNORE_NEXT_CLICK_PROPERTY = '__v-click-outside-ignore-next';

interface VClickOutsideHandler<T extends 'click' | 'pointerdown'> {
	event: T;
	srcTarget: HTMLElement;
	handler: (event: HTMLElementEventMap[T]) => void;
}

type HTMLElementWithVClickOutside = (
	HTMLElement
	& {
		[HANDLERS_PROPERTY]?: VClickOutsideHandler<'click' | 'pointerdown'>[];
		[IGNORE_NEXT_CLICK_PROPERTY]?: boolean;
	}
);
type VClickOutsideBeforeMountDirective = (el: HTMLElementWithVClickOutside, binding: Pick<DirectiveBinding<((...args: any[]) => any)>, 'value'>) => void;
type VClickOutsideUnmountedDirective = (el: HTMLElementWithVClickOutside) => void;
type VClickOutsideUpdatedDirective = (el: HTMLElementWithVClickOutside, binding: Pick<DirectiveBinding<((...args: any[]) => any)>, 'oldValue' | 'value'>) => void;

interface OnEventParams {
	el: HTMLElementWithVClickOutside;
	event: HTMLElementEventMap['click'];
	handler: ((...args: any[]) => any);
}

function validate(bindingValue: ((...args: any[]) => any)): boolean {
	if (typeof bindingValue !== 'function') {
		console.warn('v-click-outside: Binding value must be a function');
		return false;
	}

	return true;
}

function onEvent(params: OnEventParams): void {
	const {
		el,
		event,
		handler,
	} = params;
	const path = (
		(
			'path' in event
			&& event.path
		)
		|| event.composedPath?.()
	) as EventTarget[] | undefined;
	const eventTarget = event.target as HTMLElement | null;
	const shouldIgnoreNextClick = el[IGNORE_NEXT_CLICK_PROPERTY] === true;

	if (!el.isConnected) {
		unmounted(el);
		return;
	}

	if (shouldIgnoreNextClick) {
		el[IGNORE_NEXT_CLICK_PROPERTY] = false;
		return;
	}

	if (
		shouldIgnoreEvent(
			el,
			event,
		)
	) {
		return;
	}

	if (
		eventTarget
		&& path
		&& path.length > 0
	) {
		path.unshift(eventTarget);
	}

	handler(event);
}

function shouldIgnoreEvent(
	el: HTMLElementWithVClickOutside,
	event: HTMLElementEventMap['click'],
): boolean {
	const eventTarget = event.target as HTMLElement | null;

	return (
		!eventTarget
		|| el.contains(eventTarget)
		|| el.isSameNode(eventTarget)
		|| event.defaultPrevented
		|| !eventTarget.isConnected
	);
}

const beforeMount: VClickOutsideBeforeMountDirective = (
	el,
	{ value },
) => {
	if (!validate(value)) {
		return;
	}

	el[HANDLERS_PROPERTY] = (
		el[HANDLERS_PROPERTY]
		?? []
	);
	el[HANDLERS_PROPERTY].push({
		event: 'click',
		srcTarget: document.documentElement,
		handler: (event) => onEvent({
			el,
			event,
			handler: value,
		}),
	});
	el[HANDLERS_PROPERTY].push({
		event: 'pointerdown',
		srcTarget: document.documentElement,
		handler: (event) => {
			el[IGNORE_NEXT_CLICK_PROPERTY] = shouldIgnoreEvent(
				el,
				event,
			);
		},
	});
	setTimeout(
		() => {
			if (!el[HANDLERS_PROPERTY]) {
				return;
			}

			for (const handlerData of el[HANDLERS_PROPERTY]) {
				const {
					event,
					srcTarget,
					handler,
				} = handlerData;
				srcTarget.removeEventListener(
					event,
					handler,
				);
				srcTarget.addEventListener(
					event,
					handler,
					{
						passive: false,
					},
				);
			}
		},
		0,
	);
};
const unmounted: VClickOutsideUnmountedDirective = (el) => {
	if (el[HANDLERS_PROPERTY]) {
		for (const handlerData of el[HANDLERS_PROPERTY]) {
			const {
				event,
				srcTarget,
				handler,
			} = handlerData;
			srcTarget.removeEventListener(
				event,
				handler,
			);
		}

		delete el[HANDLERS_PROPERTY];
		delete el[IGNORE_NEXT_CLICK_PROPERTY];
	}
};
const updated: VClickOutsideUpdatedDirective = (
	el,
	{
		value,
		oldValue,
	},
) => {
	if (
		el.isConnected
		&& JSON.stringify(value) === JSON.stringify(oldValue)
	) {
		return;
	}

	if (!validate(value)) {
		return;
	}

	unmounted(el);
	beforeMount(
		el,
		{
			value,
		},
	);
};
const directive: Directive = {
	beforeMount,
	updated,
	unmounted,
};

const plugin = {
	install(app: App): void {
		app.directive(
			'click-outside',
			directive,
		);
	},
	directive,
};

export default plugin;
